// src/prf/prfGrid.js
// The PRF grid lattice, and where a piece of ground physically sits on it.
//
// A PRF grid id IS the NOAA CPC 0.25° raster index: latitude origin 20.125N,
// longitude origin 230.125E, 300 columns. The forward direction (lat/lon → id)
// existed only inside api/m13-sync.js and api/nau-sync.js, as TWO copies; the
// inverse (id → box) existed only inside src/App.jsx. Every PRF Advisor
// workflow needs both, and a third copy is how this repo's rules drift — so
// both live here, and sql/032 already refuses to migrate unless the inverse
// reproduces every stored row.
//
// ═══════════════════════════════════════════════════════════════════
// PHYSICAL GRID EXPOSURE IS NOT INSURED GRID ASSIGNMENT
// ═══════════════════════════════════════════════════════════════════
//
// `gridExposure()` answers ONE question: where does this ground physically lie
// on RMA's lattice. That is a measurement, and it is the only thing this file
// is allowed to decide.
//
// It is NOT the insured assignment. A PRF policy insures acres against a
// NAMED grid at a stated percent of value, and which grid a producer's acres
// are written to is a decision on the application — it can differ from where a
// field physically sits, and RMA does not require them to agree.
//
// **A GRID-CROSSING FIELD IS NEVER SPLIT AUTOMATICALLY.** 612 fields covering
// 15,264 acres in this book straddle a grid line. Splitting them by area would
// manufacture two insured units out of one field and put a number on an
// application that nobody chose. `gridExposure` REPORTS every grid a field
// touches with the acres in each and sets `straddles: true`; deciding what to
// insure where is a human act, and the UI must ask for it.

const { ringAreaM2, M2_PER_ACRE } = require("../geoArea");

const PRF_CPC_LAT0 = 20.125;
const PRF_CPC_LON0 = 230.125;
const PRF_CPC_STEP = 0.25;
const PRF_CPC_COLS = 300;

// lat/lon → grid id. `lon` may be signed (-77.5) or CPC-style east (282.5).
function prfGridFromLatLon(lat, lon) {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  const latIdx = Math.round((lat - PRF_CPC_LAT0) / PRF_CPC_STEP);
  const cpcLon = lon < 0 ? lon + 360 : lon;
  const lonIdx = Math.round((cpcLon - PRF_CPC_LON0) / PRF_CPC_STEP);
  const g = latIdx * PRF_CPC_COLS + lonIdx + 1;
  return g > 0 ? g : null;
}

// grid id → the cell's centre point.
function prfGridCentre(gridId) {
  const g = Number(gridId);
  if (!Number.isFinite(g) || g < 1) return null;
  return {
    lat: PRF_CPC_LAT0 + PRF_CPC_STEP * Math.floor((g - 1) / PRF_CPC_COLS),
    lon: PRF_CPC_LON0 + PRF_CPC_STEP * ((g - 1) % PRF_CPC_COLS) - 360,
  };
}

// grid id → its bounding box. The centre plus half a step each way.
function prfGridBox(gridId) {
  const c = prfGridCentre(gridId);
  if (!c) return null;
  const h = PRF_CPC_STEP / 2;
  return { minLat: c.lat - h, maxLat: c.lat + h, minLon: c.lon - h, maxLon: c.lon + h };
}

// Sutherland–Hodgman clip of one ring against an axis-aligned lat/lon box.
// Grid cells ARE axis-aligned in this coordinate system, which is what makes
// the simple algorithm exact here rather than an approximation.
function clipRingToBox(ring, box) {
  const inside = (p, edge) => {
    if (edge === 0) return p[0] >= box.minLon;
    if (edge === 1) return p[0] <= box.maxLon;
    if (edge === 2) return p[1] >= box.minLat;
    return p[1] <= box.maxLat;
  };
  const intersect = (a, b, edge) => {
    const t = edge === 0 ? (box.minLon - a[0]) / (b[0] - a[0])
            : edge === 1 ? (box.maxLon - a[0]) / (b[0] - a[0])
            : edge === 2 ? (box.minLat - a[1]) / (b[1] - a[1])
            :              (box.maxLat - a[1]) / (b[1] - a[1]);
    return [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])];
  };
  let out = ring.slice();
  for (let edge = 0; edge < 4 && out.length; edge++) {
    const input = out; out = [];
    for (let i = 0; i < input.length; i++) {
      const cur = input[i], prev = input[(i + input.length - 1) % input.length];
      const curIn = inside(cur, edge), prevIn = inside(prev, edge);
      if (curIn) {
        if (!prevIn) out.push(intersect(prev, cur, edge));
        out.push(cur);
      } else if (prevIn) {
        out.push(intersect(prev, cur, edge));
      }
    }
  }
  return out;
}

function polysOf(geom) {
  if (!geom) return null;
  if (geom.type === "MultiPolygon") return geom.coordinates;
  if (geom.type === "Polygon") return [geom.coordinates];
  return null;
}

// Every grid a geometry touches, with the acres inside each.
//
// Returns { total_acres, grids: [{ grid, acres, share }], straddles, method }
// sorted by acres descending, or null when the geometry is unusable — never an
// empty result that could be summed as zero.
function gridExposure(geom) {
  const polys = polysOf(geom);
  if (!polys) return null;

  // Which cells can possibly be touched: the geometry's own bounding box walked
  // over the lattice. Bounded by construction — a field spans a handful of
  // cells, and a bbox wide enough to span hundreds is a bad coordinate, which
  // is refused rather than looped over.
  let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
  for (const rings of polys) {
    for (const p of (rings[0] || [])) {
      if (p[1] < minLat) minLat = p[1]; if (p[1] > maxLat) maxLat = p[1];
      if (p[0] < minLon) minLon = p[0]; if (p[0] > maxLon) maxLon = p[0];
    }
  }
  if (!Number.isFinite(minLat) || !Number.isFinite(minLon)) return null;

  const g0 = prfGridFromLatLon(minLat, minLon), g1 = prfGridFromLatLon(maxLat, maxLon);
  if (!g0 || !g1) return null;
  const row0 = Math.floor((g0 - 1) / PRF_CPC_COLS), row1 = Math.floor((g1 - 1) / PRF_CPC_COLS);
  const col0 = (g0 - 1) % PRF_CPC_COLS, col1 = (g1 - 1) % PRF_CPC_COLS;
  const rows = Math.abs(row1 - row0) + 1, cols = Math.abs(col1 - col0) + 1;
  // 400 cells is 5°x5° of ground. Nothing a producer insures is that wide, so a
  // span this large is a coordinate error and gets refused, not measured.
  if (rows * cols > 400) return null;

  const byGrid = new Map();
  let total = 0;
  for (let r = Math.min(row0, row1); r <= Math.max(row0, row1); r++) {
    for (let c = Math.min(col0, col1); c <= Math.max(col0, col1); c++) {
      const grid = r * PRF_CPC_COLS + c + 1;
      const box = prfGridBox(grid);
      let m2 = 0;
      for (const rings of polys) {
        if (!rings || !rings[0] || rings[0].length < 3) continue;
        const shell = clipRingToBox(rings[0], box);
        if (shell.length < 3) continue;
        let a = Math.abs(ringAreaM2(shell));
        // Holes clipped to the same cell, so a doughnut is not counted as solid.
        for (let h = 1; h < rings.length; h++) {
          const hole = clipRingToBox(rings[h], box);
          if (hole.length >= 3) a -= Math.abs(ringAreaM2(hole));
        }
        if (a > 0) m2 += a;
      }
      if (m2 > 0) { byGrid.set(grid, (byGrid.get(grid) || 0) + m2); total += m2; }
    }
  }
  if (!byGrid.size || total <= 0) return null;

  const grids = [...byGrid.entries()]
    .map(([grid, m2]) => ({ grid, acres: m2 / M2_PER_ACRE, share: m2 / total }))
    .sort((a, b) => b.acres - a.acres || a.grid - b.grid);

  return {
    total_acres: total / M2_PER_ACRE,
    grids,
    // Reported, never acted on. See the header: the split is a human decision.
    straddles: grids.length > 1,
    method: "polygon_intersection",
  };
}

// The fallback when a field has no polygon — 65% of PRF CLUs in this book.
// A point lands in exactly one cell, so it can never straddle, and it says so:
// `method` is what tells a screen whether the split is measured or merely
// assumed to be absent.
function gridExposureFromPoint(lat, lon, acres) {
  const grid = prfGridFromLatLon(lat, lon);
  if (!grid) return null;
  const ac = Number(acres);
  return {
    total_acres: Number.isFinite(ac) ? ac : null,
    grids: [{ grid, acres: Number.isFinite(ac) ? ac : null, share: 1 }],
    straddles: false,
    method: "centroid_only",
  };
}

module.exports = {
  PRF_CPC_LAT0, PRF_CPC_LON0, PRF_CPC_STEP, PRF_CPC_COLS,
  prfGridFromLatLon, prfGridCentre, prfGridBox,
  clipRingToBox, gridExposure, gridExposureFromPoint,
};
